import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Checkbox, Empty, Input, Modal, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";

export interface GroupMemberItem {
  mediaId: number;
  name: string;
  thumbnailUrl: string;
  /** 현재 소속 그룹 id (없으면 null) */
  mediaGroupId: number | null;
  /** 현재 소속 그룹명. 대상 그룹이 아닌 다른 그룹이면 "○○ 에서 이동" 안내에 쓴다 */
  currentGroupName?: string | null;
}

interface Props {
  open: boolean;
  /** 편집 대상 그룹 */
  mediaGroupId: number;
  groupName: string;
  itemLabel: string;
  /** 해당 미디어 종류 전체 (그룹 소속 여부 무관) */
  items: GroupMemberItem[];
  onCancel: () => void;
  /** 체크된 항목 전체가 곧 이 그룹의 새 멤버 목록이 된다 */
  onSubmit: (mediaIds: number[]) => Promise<void>;
}

/**
 * 그룹 편집 모달.
 * 해당 종류의 미디어를 전부 보여주고, 체크 상태로 그룹 멤버를 정한다.
 * 체크 해제 = 그룹에서 제외, 체크 = 추가(다른 그룹 소속이면 그쪽에서 이동).
 * 저장 시 체크된 목록 전체를 PUT /items 로 한 번에 교체한다(원자적).
 */
const MediaGroupMembersModal = ({
  open,
  mediaGroupId,
  groupName,
  itemLabel,
  items,
  onCancel,
  onSubmit,
}: Props) => {
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [keyword, setKeyword] = useState("");
  const [saving, setSaving] = useState(false);

  // 현재 이 그룹에 속한 항목을 체크 상태의 초기값으로 잡는다
  const initialMembers = useMemo(
    () => items.filter((it) => it.mediaGroupId === mediaGroupId).map((it) => it.mediaId),
    [items, mediaGroupId]
  );

  useEffect(() => {
    if (open) {
      setChecked(new Set(initialMembers));
      setKeyword("");
    }
  }, [open, initialMembers]);

  const visible = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return items;
    return items.filter((it) => (it.name ?? "").toLowerCase().includes(kw));
  }, [items, keyword]);

  const toggle = (mediaId: number) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(mediaId)) next.delete(mediaId);
      else next.add(mediaId);
      return next;
    });
  };

  const allVisibleChecked = visible.length > 0 && visible.every((it) => checked.has(it.mediaId));

  const toggleAllVisible = () => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (allVisibleChecked) visible.forEach((it) => next.delete(it.mediaId));
      else visible.forEach((it) => next.add(it.mediaId));
      return next;
    });
  };

  // 변경 요약 (저장 전에 무엇이 바뀌는지 보여준다)
  const initialSet = useMemo(() => new Set(initialMembers), [initialMembers]);
  const addedCount = useMemo(
    () => Array.from(checked).filter((id) => !initialSet.has(id)).length,
    [checked, initialSet]
  );
  const removedCount = useMemo(
    () => initialMembers.filter((id) => !checked.has(id)).length,
    [initialMembers, checked]
  );
  const isDirty = addedCount > 0 || removedCount > 0;

  const handleOk = async () => {
    if (!isDirty) {
      onCancel();
      return;
    }
    setSaving(true);
    try {
      await onSubmit(Array.from(checked));
    } catch (e: any) {
      message.error(e?.message || "그룹 편집에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={`'${groupName}' 그룹 편집`}
      okText="저장"
      cancelText="취소"
      okButtonProps={{ disabled: !isDirty }}
      confirmLoading={saving}
      onOk={handleOk}
      onCancel={onCancel}
      centered
      destroyOnClose
      width={560}
    >
      {items.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={`이 게시글에 ${itemLabel}가 없습니다.`}
        />
      ) : (
        <>
          <Desc>
            체크한 {itemLabel}가 이 그룹의 멤버가 됩니다.
            체크를 해제하면 그룹에서 빠지고, 다른 그룹 소속을 체크하면 그쪽에서 옮겨옵니다.
          </Desc>

          <Toolbar>
            <Input
              size="small"
              allowClear
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              placeholder="이름으로 검색"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
            <SelectAll onClick={toggleAllVisible} type="button" disabled={visible.length === 0}>
              {allVisibleChecked ? "전체 해제" : "전체 선택"}
            </SelectAll>
          </Toolbar>

          <ScrollBody>
            {visible.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="검색 결과가 없습니다." />
            ) : (
              visible.map((it) => {
                const isChecked = checked.has(it.mediaId);
                const wasMember = initialSet.has(it.mediaId);
                const fromOther = it.mediaGroupId != null && it.mediaGroupId !== mediaGroupId;
                return (
                  <Row key={it.mediaId} $checked={isChecked} onClick={() => toggle(it.mediaId)}>
                    <Checkbox
                      checked={isChecked}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggle(it.mediaId)}
                    />
                    <Thumb src={it.thumbnailUrl} alt={it.name} loading="lazy" />
                    <Name title={it.name}>{it.name || "이름 없음"}</Name>
                    {isChecked && !wasMember && fromOther && (
                      <Chip $tone="move">{it.currentGroupName} 에서 이동</Chip>
                    )}
                    {isChecked && !wasMember && !fromOther && <Chip $tone="add">추가</Chip>}
                    {!isChecked && wasMember && <Chip $tone="remove">제외</Chip>}
                  </Row>
                );
              })
            )}
          </ScrollBody>

          <Summary>
            멤버 <b>{initialMembers.length}</b>개 → <b>{checked.size}</b>개
            {isDirty && (
              <Delta>
                {addedCount > 0 && <span className="add">+{addedCount}</span>}
                {removedCount > 0 && <span className="remove">-{removedCount}</span>}
              </Delta>
            )}
          </Summary>
        </>
      )}
    </Modal>
  );
};

const Desc = styled.div`
  margin-bottom: 12px;
  font-size: 13px;
  color: #595959;
  line-height: 1.6;
  word-break: keep-all;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const SelectAll = styled.button`
  flex-shrink: 0;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  font-size: 12px;
  color: #1677ff;

  &:disabled {
    color: #bfbfbf;
    cursor: default;
  }
`;

const ScrollBody = styled.div`
  max-height: 48vh;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
`;

const Row = styled.div<{ $checked: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 10px;
  cursor: pointer;
  background: ${({ $checked }) => ($checked ? "#e6f4ff" : "transparent")};
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: ${({ $checked }) => ($checked ? "#d8ecff" : "#fafafa")};
  }
`;

const Thumb = styled.img`
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 3px;
  background: #f0f2f5;
  flex-shrink: 0;
`;

const Name = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Chip = styled.span<{ $tone: "add" | "remove" | "move" }>`
  flex-shrink: 0;
  padding: 1px 7px;
  border-radius: 8px;
  font-size: 11px;
  line-height: 1.6;
  ${({ $tone }) =>
    $tone === "add"
      ? "background:#f6ffed; color:#389e0d;"
      : $tone === "remove"
      ? "background:#fff1f0; color:#cf1322;"
      : "background:#fff7e6; color:#ad6800;"}
`;

const Summary = styled.div`
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #595959;

  b {
    color: #16202b;
    font-variant-numeric: tabular-nums;
  }
`;

const Delta = styled.span`
  display: inline-flex;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;

  .add {
    color: #389e0d;
  }
  .remove {
    color: #cf1322;
  }
`;

export default MediaGroupMembersModal;
